import type { LensDataset } from '~/types/leLens';

/**
 * Turns a raw column name into a reader-facing filter label. Underscores
 * become spaces, every word is title-cased, and a trailing "bk" segment is
 * dropped, since that suffix names a warehouse business key and carries no
 * meaning for a viewer. A caller-supplied declared label always wins over the
 * derived one, because an editor who named a column on purpose knows better
 * than a mechanical rule.
 */
export function humaniseFilterLabel(column: string, declaredLabel?: string): string {
  if (declaredLabel && declaredLabel.trim()) {
    return declaredLabel.trim();
  }
  const words = column.trim().replace(/_/g, ' ').toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length > 1 && words[words.length - 1] === 'bk') {
    words.pop();
  }
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

/**
 * The first declared label a dataset offers for this column, if any dataset
 * on the dashboard names one. A column can be pooled across several datasets,
 * so this takes the first declaration found rather than requiring every
 * dataset to agree.
 */
export function declaredDatasetLabel(column: string, datasets: Array<LensDataset>): string | undefined {
  for (const dataset of datasets) {
    const declared =
      dataset.columns?.find((field) => field.name === column)?.label ?? dataset.dimensionLabels?.[column];
    if (declared && declared.trim()) {
      return declared;
    }
  }
  return undefined;
}

/** The first governed plain-English definition published for a column. */
export function declaredDatasetDescription(column: string, datasets: Array<LensDataset>): string | undefined {
  for (const dataset of datasets) {
    const description = dataset.columns?.find((field) => field.name === column)?.description;
    if (description?.trim()) {
      return description.trim();
    }
  }
  return undefined;
}
